import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  CANCELLABLE_SHIPMENT_STATUSES,
  CARRIER_FIELDS,
  CARRIER_NAMES,
  CARRIER_SERVICES,
  type CarrierAccountDto,
  type CarrierId,
  type CarrierRuleDto,
  type ChargeSummaryRow,
  type ShopShippingRateDto,
  type Settings,
  type ShipmentDetailDto,
  type ShipmentDto,
  type WarehouseDto,
} from '@thebb/shared';
import { del, get, post, put } from '../api';
import { useAuth } from '../auth';
import { Badge, ErrorBox, Modal, PageTitle, Spinner, fmtTime } from '../components/ui';
import { modeLabel, shipmentStatusLabel, shipmentTone } from '../components/Shipping';

type Tab = 'shipments' | 'charges' | 'accounts' | 'rules' | 'settings';
const destinationLabel: Record<string, string> = { ANY: 'Alle lande', DK: 'Danmark', EU: 'EU uden DK', WORLD: 'Uden for EU' };

/** Milestone 4: shipments, carrier accounts, routing rules and the customs details. */
export function ShippingPage() {
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('shipments');
  const [error, setError] = useState<unknown>(null);
  const tabs: [Tab, string, boolean][] = [
    ['shipments', 'Forsendelser', true],
    ['charges', 'Fragtopgørelse', can('shops.view')],
    ['accounts', 'Fragtfirmaer', can('shipping.manage')],
    ['rules', 'Regler', true],
    ['settings', 'Told og afsender', can('settings.manage')],
  ];
  return (
    <div className="space-y-4">
      <PageTitle title="Fragt" subtitle="GLS til pakker, Danske Fragtmænd til paller i Danmark, Dachser til paller i udlandet" />
      <div className="flex gap-1 border-b border-line">
        {tabs.filter((t) => t[2]).map(([k, label]) => (
          <button key={k} className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted hover:text-ink'}`} onClick={() => { setTab(k); setError(null); }}>{label}</button>
        ))}
      </div>
      <ErrorBox error={error} />
      {tab === 'shipments' && <ShipmentsTab onError={setError} />}
      {tab === 'charges' && <ChargesTab onError={setError} />}
      {tab === 'accounts' && <AccountsTab onError={setError} />}
      {tab === 'rules' && <RulesTab onError={setError} />}
      {tab === 'settings' && <CustomsSettings onError={setError} />}
    </div>
  );
}

function ShipmentsTab({ onError }: { onError: (e: unknown) => void }) {
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [carrier, setCarrier] = useState('');
  const [open, setOpen] = useState<string | null>(null);
  const qc = useQueryClient();
  const { can } = useAuth();
  const cancel = useMutation({
    mutationFn: (id: string) => post(`/shipping/shipments/${id}/cancel`),
    onSuccess: () => { onError(null); void qc.invalidateQueries({ queryKey: ['shipments'] }); },
    onError,
  });
  const params = useMemo(() => { const p = new URLSearchParams({ perPage: '100' }); if (q) p.set('q', q); if (status) p.set('status', status); if (carrier) p.set('carrier', carrier); return p.toString(); }, [q, status, carrier]);
  const list = useQuery({ queryKey: ['shipments', params], queryFn: () => get<{ items: ShipmentDto[]; total: number }>(`/shipping/shipments?${params}`), refetchInterval: 30_000 });
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        <input className="input w-60" placeholder="Søg nr., fragtbrev, modtager" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="input w-44" value={status} onChange={(e) => setStatus(e.target.value)}><option value="">Alle statusser</option>{Object.entries(shipmentStatusLabel).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <select className="input w-48" value={carrier} onChange={(e) => setCarrier(e.target.value)}><option value="">Alle fragtfirmaer</option>{Object.entries(CARRIER_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <span className="self-center text-sm text-muted">{list.data?.total ?? 0} forsendelser</span>
      </div>
      {list.isLoading ? <Spinner /> : (
        <table className="table card p-0">
          <thead><tr><th>Nr.</th><th>Ordre / batch</th><th>Modtager</th><th>Fragtfirma</th><th>Kolli</th><th>Fragtbrev</th><th>Status</th><th>Booket</th><th></th></tr></thead>
          <tbody>
            {list.data?.items.map((s) => (
              <tr key={s.id} className="cursor-pointer hover:bg-canvas" onClick={() => setOpen(s.id)}>
                <td className="font-medium">{s.number}</td>
                <td>{s.orderId ? <Link to="/orders/$id" params={{ id: s.orderId }} className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>{s.orderNumber}</Link> : s.ownerType === 'batch' ? <Link to="/batches/$id" params={{ id: s.ownerId }} className="text-primary hover:underline" onClick={(e) => e.stopPropagation()}>{s.ownerNumber}</Link> : s.ownerNumber}</td>
                <td className="text-xs">{[s.receiver.company, s.receiver.name].filter(Boolean).join(' · ')}<div className="text-muted">{s.receiver.zip} {s.receiver.city} {s.receiver.countryCode}</div></td>
                <td>{CARRIER_NAMES[s.carrier]}<div className="text-xs text-muted">{s.service}{s.mode !== 'live' ? ` · ${modeLabel[s.mode]}` : ''}</div></td>
                <td className="whitespace-nowrap tabular-nums">{s.packageCount} · {(s.weightG / 1000).toFixed(1)} kg</td>
                <td className="tabular-nums">{s.consignmentNumber ?? '-'}</td>
                <td><Badge tone={shipmentTone(s.status)}>{shipmentStatusLabel[s.status]}</Badge></td>
                <td className="whitespace-nowrap text-xs text-muted">{fmtTime(s.bookedAt ?? s.createdAt)}</td>
                <td className="text-right">
                  {CANCELLABLE_SHIPMENT_STATUSES.includes(s.status) && can('shipping.cancel') && (
                    <button
                      className="btn"
                      disabled={cancel.isPending}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm(`Annullér forsendelse ${s.number}${s.consignmentNumber ? ` (fragtbrev ${s.consignmentNumber})` : ''}?`)) cancel.mutate(s.id);
                      }}
                    >
                      Annullér
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {list.data?.items.length === 0 && <tr><td colSpan={9} className="text-muted">Ingen forsendelser endnu. Book fragt fra Pak eller en pakkebatch.</td></tr>}
          </tbody>
        </table>
      )}
      {open && <ShipmentModal id={open} onClose={() => setOpen(null)} onError={onError} />}
    </div>
  );
}

function ShipmentModal({ id, onClose, onError }: { id: string; onClose: () => void; onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const d = useQuery({ queryKey: ['shipment', id], queryFn: () => get<ShipmentDetailDto>(`/shipping/shipments/${id}`) });
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['shipment', id] }); void qc.invalidateQueries({ queryKey: ['shipments'] }); };
  const track = useMutation({ mutationFn: () => post(`/shipping/shipments/${id}/track`), onSuccess: refresh, onError });
  const cancel = useMutation({ mutationFn: () => post(`/shipping/shipments/${id}/cancel`), onSuccess: refresh, onError });
  const [logOpen, setLogOpen] = useState<string | null>(null);
  const s = d.data;
  return (
    <Modal title={s ? `Forsendelse ${s.number}` : 'Forsendelse'} onClose={onClose}>
      {!s ? <Spinner /> : (
        <div className="space-y-3 text-sm">
          <div className="flex flex-wrap items-center gap-2">
            <Badge tone={shipmentTone(s.status)}>{shipmentStatusLabel[s.status]}</Badge>
            <span>{CARRIER_NAMES[s.carrier]} · {s.service}</span>
            {s.mode !== 'live' && <Badge tone="amber">{modeLabel[s.mode]}</Badge>}
            {s.trackingUrl ? <a className="text-primary hover:underline" href={s.trackingUrl} target="_blank" rel="noreferrer">{s.consignmentNumber}</a> : <span>{s.consignmentNumber}</span>}
            {s.priceMinor != null && <span className="text-muted">{(s.priceMinor / 100).toFixed(2)} kr</span>}
          </div>
          {s.error && <div className="rounded-lg bg-red-50 p-2 text-red-800">{s.error}</div>}
          <div>{[s.receiver.company, s.receiver.name, s.receiver.street1, s.receiver.street2, `${s.receiver.zip ?? ''} ${s.receiver.city ?? ''}`, s.receiver.countryCode].filter(Boolean).join(', ')}</div>
          {s.trackingNumbers.length > 0 && <div className="text-muted">Kolli: {s.trackingNumbers.join(', ')}</div>}
          <div className="flex flex-wrap gap-2">
            {s.labelDocumentIds.map((doc) => <a key={doc} className="btn" href={`/api/documents/${doc}/download`} target="_blank" rel="noreferrer">Label (PDF)</a>)}
            {s.customsDocumentId && <a className="btn" href={`/api/documents/${s.customsDocumentId}/download`} target="_blank" rel="noreferrer">Toldfaktura (PDF)</a>}
            {['labelled', 'handed_over', 'in_transit'].includes(s.status) && s.mode !== 'dry_run' && can('shipping.book') && <button className="btn" disabled={track.isPending} onClick={() => track.mutate()}>Hent sporing nu</button>}
            {CANCELLABLE_SHIPMENT_STATUSES.includes(s.status) && can('shipping.cancel') && <button className="btn" disabled={cancel.isPending} onClick={() => confirm('Annullér forsendelsen?') && cancel.mutate()}>Annullér</button>}
          </div>
          <div>
            <div className="label">Sporing</div>
            {s.events.length === 0 ? <div className="text-muted">{s.mode === 'dry_run' ? 'Testkørsler spores ikke.' : 'Ingen hændelser endnu; systemet henter sporing hver time.'}</div> : (
              <ul className="space-y-1">{s.events.map((e, i) => <li key={i}><span className="text-muted">{fmtTime(e.at)}</span> {e.description}{e.location ? <span className="text-muted"> · {e.location}</span> : null}</li>)}</ul>
            )}
          </div>
          <div>
            <div className="label">Log over kald til fragtfirmaet</div>
            <ul className="space-y-1">
              {s.log.map((l) => (
                <li key={l.id}>
                  <button className="text-left hover:underline" onClick={() => setLogOpen(logOpen === l.id ? null : l.id)}>
                    <span className="text-muted">{fmtTime(l.at)}</span> {l.operation} · <span className={l.outcome === 'ok' ? 'text-success' : 'text-red-700'}>{l.outcome}</span> · {l.durationMs} ms{l.error ? ` · ${l.error}` : ''}
                  </button>
                  {logOpen === l.id && <pre className="mt-1 max-h-64 overflow-auto rounded-lg bg-canvas p-2 text-xs">{JSON.stringify({ request: l.request, response: l.response }, null, 2)}</pre>}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </Modal>
  );
}

function AccountsTab({ onError }: { onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const accounts = useQuery({ queryKey: ['carrier-accounts'], queryFn: () => get<CarrierAccountDto[]>('/shipping/accounts') });
  const [editing, setEditing] = useState<CarrierAccountDto | 'new' | null>(null);
  const [copying, setCopying] = useState<CarrierAccountDto | null>(null);
  const remove = useMutation({ mutationFn: (id: string) => del(`/shipping/accounts/${id}`), onSuccess: () => void qc.invalidateQueries({ queryKey: ['carrier-accounts'] }), onError });
  return (
    <div className="space-y-3">
      <div className="text-sm text-muted">
        Testkørsel bygger og tjekker bestillingen og printer vores egen testlabel uden at kontakte fragtfirmaet. Testmiljø bruger fragtfirmaets test-API (Danske Fragtmænd har et). Live booker rigtigt. Adgangskoder gemmes krypteret og vises aldrig igen.
      </div>
      <table className="table card p-0">
        <thead><tr><th>Fragtfirma</th><th>Navn</th><th>Lager</th><th>Tilstand</th><th>Oplysninger</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {accounts.data?.map((a) => {
            const defs = CARRIER_FIELDS[a.carrier];
            const filled = defs.filter((f) => a.fields[f.key]).length;
            return (
              <tr key={a.id} className={a.active ? '' : 'text-muted'}>
                <td className="font-medium">{CARRIER_NAMES[a.carrier]}</td>
                <td>{a.name}</td>
                <td>{a.warehouseCode ?? 'Alle'}</td>
                <td><Badge tone={a.mode === 'live' ? 'green' : 'amber'}>{modeLabel[a.mode]}</Badge></td>
                <td className="text-xs">{filled}/{defs.length} udfyldt{a.baseUrl ? ` · ${a.baseUrl}` : ''}</td>
                <td>{a.active ? <Badge tone="green">aktiv</Badge> : <Badge>slået fra</Badge>}</td>
                <td className="whitespace-nowrap text-right"><button className="btn" onClick={() => setEditing(a)}>Redigér</button> <button className="btn" onClick={() => setCopying(a)}>Duplikér</button> <button className="btn" onClick={() => confirm(`Slet ${a.name}? Har den forsendelser, slås den bare fra.`) && remove.mutate(a.id)}>Slet</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <button className="btn btn-primary" onClick={() => setEditing('new')}>Tilføj konto</button>
      {editing && <AccountModal account={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onError={onError} />}
      {copying && <AccountModal account={null} copyOf={copying} onClose={() => setCopying(null)} onError={onError} />}
    </div>
  );
}

/** A carrier account, new, edited, or a copy of another (its secrets are kept on the server). */
function AccountModal({ account, copyOf, onClose, onError }: { account: CarrierAccountDto | null; copyOf?: CarrierAccountDto; onClose: () => void; onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: () => get<WarehouseDto[]>('/warehouses') });
  const from = account ?? copyOf;
  const [form, setForm] = useState({
    carrier: (from?.carrier ?? 'gls') as CarrierId,
    name: copyOf ? `${copyOf.name} (kopi)` : (account?.name ?? ''),
    warehouseId: from?.warehouseId ?? '',
    mode: from?.mode ?? 'dry_run',
    baseUrl: from?.baseUrl ?? '',
    active: from?.active ?? true,
    fields: { ...(from?.fields ?? {}) } as Record<string, string>,
  });
  const save = useMutation({
    mutationFn: () => {
      const body = { ...form, warehouseId: form.warehouseId || null, baseUrl: form.baseUrl.trim() || null, name: form.name || CARRIER_NAMES[form.carrier], ...(copyOf ? { copyFrom: copyOf.id } : {}) };
      return account ? put(`/shipping/accounts/${account.id}`, body) : post('/shipping/accounts', body);
    },
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['carrier-accounts'] }); onClose(); },
    onError,
  });
  return (
    <Modal title={account ? `Redigér ${account.name}` : copyOf ? `Kopi af ${copyOf.name}` : 'Ny fragtkonto'} onClose={onClose}>
      <div className="grid gap-3 text-sm sm:grid-cols-2">
        <div><label className="label">Fragtfirma</label><select className="input" disabled={!!from} value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value as CarrierId, fields: {} })}>{Object.entries(CARRIER_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <div><label className="label">Navn</label><input className="input" value={form.name} placeholder={CARRIER_NAMES[form.carrier]} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
        <div><label className="label">Lager (afsender)</label><select className="input" value={form.warehouseId} onChange={(e) => setForm({ ...form, warehouseId: e.target.value })}><option value="">Alle lagre</option>{warehouses.data?.filter((w) => w.type === 'operated').map((w) => <option key={w.id} value={w.id}>{w.code} · {w.name}</option>)}</select></div>
        <div>
          <label className="label">Tilstand</label>
          <select className="input" value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value as typeof form.mode })}>
            <option value="dry_run">Testkørsel (intet sendes)</option>
            <option value="test">Fragtfirmaets testmiljø</option>
            <option value="live">Live</option>
          </select>
        </div>
        {CARRIER_FIELDS[form.carrier].map((f) => (
          <div key={f.key}>
            <label className="label">{f.label}</label>
            {f.options ? (
              <select className="input" value={form.fields[f.key] || f.options[0]![0]} onChange={(e) => setForm({ ...form, fields: { ...form.fields, [f.key]: e.target.value } })}>
                {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            ) : (
              <input className="input" type={f.secret ? 'password' : 'text'} autoComplete="off" value={form.fields[f.key] ?? ''} placeholder={f.hint ?? ''} onChange={(e) => setForm({ ...form, fields: { ...form.fields, [f.key]: e.target.value } })} />
            )}
          </div>
        ))}
        <div className="sm:col-span-2"><label className="label">API-adresse (tom = standard)</label><input className="input" value={form.baseUrl} placeholder={form.carrier === 'gls' ? 'https://ws.gls.dk/ws/DK/V1' : form.carrier === 'danske_fragtmaend' ? 'https://apitest.fragt.dk eller https://api.fragt.dk' : ''} onChange={(e) => setForm({ ...form, baseUrl: e.target.value })} /></div>
        <label className="flex items-center gap-2 sm:col-span-2"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Aktiv</label>
      </div>
      <div className="mt-4 flex justify-end gap-2"><button className="btn" onClick={onClose}>Annullér</button><button className="btn btn-primary" disabled={save.isPending} onClick={() => save.mutate()}>Gem</button></div>
    </Modal>
  );
}

function RulesTab({ onError }: { onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const rules = useQuery({ queryKey: ['carrier-rules'], queryFn: () => get<CarrierRuleDto[]>('/shipping/rules') });
  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: () => get<WarehouseDto[]>('/warehouses') });
  const [form, setForm] = useState({ priority: '50', packageKind: 'parcel', destination: 'ANY', warehouseId: '', carrier: 'gls' as CarrierId, service: '' });
  const set = (data: CarrierRuleDto[]) => qc.setQueryData(['carrier-rules'], data);
  const create = useMutation({ mutationFn: () => post<CarrierRuleDto[]>('/shipping/rules', { ...form, priority: Number(form.priority) || 100, warehouseId: form.warehouseId || null, service: form.service || null }), onSuccess: set, onError });
  const toggle = useMutation({ mutationFn: (r: CarrierRuleDto) => put<CarrierRuleDto[]>(`/shipping/rules/${r.id}`, { ...r, active: !r.active }), onSuccess: set, onError });
  const remove = useMutation({ mutationFn: (r: CarrierRuleDto) => del<CarrierRuleDto[]>(`/shipping/rules/${r.id}`), onSuccess: set, onError });
  const manage = can('shipping.manage');
  return (
    <div className="space-y-3">
      <div className="text-sm text-muted">Den første aktive regel (laveste prioritet) der passer på kolli-typen, destinationen, shoppen og lageret vælger fragtfirmaet. Pakkeren kan altid vælge et andet ved bookingen.</div>
      <table className="table card p-0">
        <thead><tr><th className="text-right">Prioritet</th><th>Kolli</th><th>Til</th><th>Shop</th><th>Lager</th><th>Fragtfirma</th><th>Service</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {rules.data?.map((r) => (
            <tr key={r.id} className={r.active ? '' : 'text-muted'}>
              <td className="text-right tabular-nums">{r.priority}</td>
              <td>{r.packageKind === 'pallet' ? 'Paller' : 'Pakker'}</td>
              <td>{destinationLabel[r.destination] ?? r.destination}</td>
              <td>{r.shopName ?? 'Alle'}</td>
              <td>{r.warehouseCode ?? 'Alle'}</td>
              <td>{CARRIER_NAMES[r.carrier]}</td>
              <td>{CARRIER_SERVICES[r.carrier].find((s) => s.code === r.service)?.name ?? r.service ?? 'Standard'}</td>
              <td>{r.active ? <Badge tone="green">aktiv</Badge> : <Badge>slået fra</Badge>}</td>
              <td className="whitespace-nowrap text-right">{manage && <><button className="btn" onClick={() => toggle.mutate(r)}>{r.active ? 'Slå fra' : 'Slå til'}</button> <button className="btn" onClick={() => confirm('Slet reglen?') && remove.mutate(r)}>Slet</button></>}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {manage && (
        <div className="card flex flex-wrap items-end gap-3 text-sm">
          <div><label className="label">Prioritet</label><input className="input w-20" type="number" value={form.priority} onChange={(e) => setForm({ ...form, priority: e.target.value })} /></div>
          <div><label className="label">Kolli</label><select className="input w-28" value={form.packageKind} onChange={(e) => setForm({ ...form, packageKind: e.target.value })}><option value="parcel">Pakker</option><option value="pallet">Paller</option></select></div>
          <div><label className="label">Til</label><input className="input w-40" list="dest" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value.toUpperCase() })} /><datalist id="dest"><option value="ANY" /><option value="DK" /><option value="EU" /><option value="WORLD" /><option value="NO,CH,GB" /></datalist></div>
          <div><label className="label">Lager</label><select className="input w-28" value={form.warehouseId} onChange={(e) => setForm({ ...form, warehouseId: e.target.value })}><option value="">Alle</option>{warehouses.data?.filter((w) => w.type === 'operated').map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}</select></div>
          <div><label className="label">Fragtfirma</label><select className="input w-44" value={form.carrier} onChange={(e) => setForm({ ...form, carrier: e.target.value as CarrierId, service: '' })}>{Object.entries(CARRIER_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
          <div><label className="label">Service</label><select className="input w-60" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })}><option value="">Standard</option>{CARRIER_SERVICES[form.carrier].map((s) => <option key={s.code} value={s.code}>{s.name}</option>)}</select></div>
          <button className="btn btn-primary" disabled={create.isPending} onClick={() => create.mutate()}>Tilføj regel</button>
        </div>
      )}
    </div>
  );
}

function CustomsSettings({ onError }: { onError: (e: unknown) => void }) {
  const qc = useQueryClient();
  const settings = useQuery({ queryKey: ['settings'], queryFn: () => get<Settings>('/settings') });
  const [form, setForm] = useState({ companyName: '', vatNumber: '', eori: '', incoterm: 'DAP', trackHours: '1' });
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    const s = settings.data;
    if (s) setForm({ companyName: s['shipping.companyName'], vatNumber: s['shipping.vatNumber'], eori: s['shipping.eori'], incoterm: s['shipping.incoterm'], trackHours: String(s['shipping.trackHours']) });
  }, [settings.data]);
  const save = useMutation({
    mutationFn: () => put('/settings', { 'shipping.companyName': form.companyName, 'shipping.vatNumber': form.vatNumber, 'shipping.eori': form.eori, 'shipping.incoterm': form.incoterm, 'shipping.trackHours': Number(form.trackHours) || 1 }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['settings'] }); setSaved(true); },
    onError,
  });
  if (!settings.data) return <Spinner />;
  return (
    <div className="card max-w-2xl space-y-3 text-sm">
      <div className="text-muted">Bruges på toldfakturaen ved forsendelser uden for EU og som afsender over for fragtfirmaerne. Afsenderadressen er lagerets adresse (Lagre & Navision).</div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="sm:col-span-2"><label className="label">Firmanavn</label><input className="input" value={form.companyName} onChange={(e) => setForm({ ...form, companyName: e.target.value })} /></div>
        <div><label className="label">CVR / momsnummer</label><input className="input" value={form.vatNumber} placeholder="DK12345678" onChange={(e) => setForm({ ...form, vatNumber: e.target.value })} /></div>
        <div><label className="label">EORI-nummer</label><input className="input" value={form.eori} placeholder="DK12345678" onChange={(e) => setForm({ ...form, eori: e.target.value })} /></div>
        <div><label className="label">Incoterm</label><select className="input" value={form.incoterm} onChange={(e) => setForm({ ...form, incoterm: e.target.value })}>{['DAP', 'DDP', 'EXW', 'FCA', 'CPT'].map((t) => <option key={t}>{t}</option>)}</select></div>
        <div><label className="label">Hent sporing hver (timer)</label><input className="input" type="number" min={1} max={24} value={form.trackHours} onChange={(e) => setForm({ ...form, trackHours: e.target.value })} /></div>
      </div>
      <div className="flex items-center gap-3"><button className="btn btn-primary" disabled={save.isPending} onClick={() => { setSaved(false); save.mutate(); }}>Gem</button>{saved && <span className="text-success">Gemt</span>}</div>
    </div>
  );
}

/** Shipments of one order, for the order page. */
export function OrderShipments({ orderId }: { orderId: string }) {
  const list = useQuery({ queryKey: ['shipments', 'order', orderId], queryFn: () => get<{ items: ShipmentDto[] }>(`/shipping/shipments?orderId=${orderId}`) });
  if (!list.data?.items.length) return null;
  return (
    <div className="card">
      <div className="label">Forsendelser</div>
      <ul className="space-y-1 text-sm">
        {list.data.items.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-2">
            <Badge tone={shipmentTone(s.status)}>{shipmentStatusLabel[s.status]}</Badge>
            <span>{CARRIER_NAMES[s.carrier]} · {s.service}</span>
            {s.trackingUrl ? <a className="text-primary hover:underline" href={s.trackingUrl} target="_blank" rel="noreferrer">{s.consignmentNumber}</a> : <span>{s.consignmentNumber}</span>}
            {s.mode !== 'live' && <Badge tone="amber">{modeLabel[s.mode]}</Badge>}
            {s.labelDocumentIds[0] && <a className="text-xs text-primary hover:underline" href={`/api/documents/${s.labelDocumentIds[0]}/download`} target="_blank" rel="noreferrer">label</a>}
            {s.customsDocumentId && <a className="text-xs text-primary hover:underline" href={`/api/documents/${s.customsDocumentId}/download`} target="_blank" rel="noreferrer">toldfaktura</a>}
          </li>
        ))}
      </ul>
    </div>
  );
}

const RATE_FIELDS: [keyof ShopShippingRateDto, string][] = [
  ['packingFullMinor', 'Pakning helpalle'], ['packingHalfMinor', 'Pakning halvpalle'], ['packingQuarterMinor', 'Pakning kvartpalle'], ['packingColliMinor', 'Pakning kolli'],
  ['freightFullMinor', 'Fragt helpalle'], ['freightHalfMinor', 'Fragt halvpalle'], ['freightQuarterMinor', 'Fragt kvartpalle'], ['freightColliMinor', 'Fragt kolli'],
];

/** The shop's prices for charging packing and freight on to its customer, as in YouWe print. */
export function ShopShippingRates({ shopId }: { shopId: string }) {
  const qc = useQueryClient();
  const { can } = useAuth();
  const rate = useQuery({ queryKey: ['shop-rate', shopId], queryFn: () => get<ShopShippingRateDto>(`/shipping/shops/${shopId}/rates`) });
  const [form, setForm] = useState<Record<string, string>>({});
  const [enabled, setEnabled] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [saved, setSaved] = useState(false);
  useEffect(() => {
    if (rate.data) {
      setEnabled(rate.data.enabled);
      setForm(Object.fromEntries(RATE_FIELDS.map(([k]) => [k, ((rate.data[k] as number) / 100).toFixed(2).replace('.', ',')])));
    }
  }, [rate.data]);
  const toMinor = (v: string | undefined) => Math.round(Number((v ?? '0').replace(/\./g, '').replace(',', '.')) * 100) || 0;
  const save = useMutation({
    mutationFn: () => put<ShopShippingRateDto>(`/shipping/shops/${shopId}/rates`, { enabled, ...Object.fromEntries(RATE_FIELDS.map(([k]) => [k, toMinor(form[k])])) }),
    onSuccess: (r) => { qc.setQueryData(['shop-rate', shopId], r); setSaved(true); setError(null); },
    onError: setError,
  });
  if (!rate.data) return null;
  const manage = can('shops.manage');
  return (
    <section className="card space-y-3 text-sm">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="m-0 text-sm font-semibold uppercase tracking-wide text-muted">Fragtpriser til kunden</h2>
        <label className="flex items-center gap-2"><input type="checkbox" disabled={!manage} checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Opkræv pakning og fragt (ekstra fragt)</label>
        <Link to="/shipping" className="ml-auto text-primary hover:underline">Fragtopgørelse</Link>
      </div>
      <ErrorBox error={error} />
      <div className="grid gap-3 sm:grid-cols-4">
        {RATE_FIELDS.map(([k, label]) => (
          <div key={k}><label className="label">{label} (kr)</label><input className="input" inputMode="decimal" disabled={!manage || !enabled} value={form[k] ?? ''} onChange={(e) => setForm({ ...form, [k]: e.target.value })} /></div>
        ))}
      </div>
      <div className="flex items-center gap-3">
        {manage && <button className="btn btn-primary" disabled={save.isPending} onClick={() => { setSaved(false); save.mutate(); }}>Gem</button>}
        {saved && <span className="text-success">Gemt</span>}
        <span className="text-muted">Prisen beregnes for hver forsendelse, når fragten bookes, og samles i fragtopgørelsen til fakturaen.</span>
      </div>
    </section>
  );
}

/** Parcels summary per shop and carrier over a period, with CSV export, and the end-of-day list. */
export function ChargesTab({ onError }: { onError: (e: unknown) => void }) {
  const shopsQ = useQuery({ queryKey: ['shops', 'all-for-charges'], queryFn: () => get<{ id: string; name: string }[]>('/shops?includeArchived=true') });
  const first = new Date();
  first.setDate(1);
  const [q, setQ] = useState({ shopId: '', from: first.toISOString().slice(0, 10), to: new Date().toISOString().slice(0, 10) });
  const params = useMemo(() => {
    const p = new URLSearchParams();
    if (q.shopId) p.set('shopId', q.shopId);
    if (q.from) p.set('from', q.from);
    if (q.to) p.set('to', q.to);
    return p.toString();
  }, [q]);
  const summary = useQuery({ queryKey: ['charges', params], queryFn: () => get<ChargeSummaryRow[]>(`/shipping/charges/summary?${params}`) });
  const kr = (m: number) => (m / 100).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const rows = summary.data ?? [];
  const tot = rows.reduce((t, r) => ({ packing: t.packing + r.packingMinor, freight: t.freight + r.freightMinor, paid: t.paid + r.shopPaidMinor }), { packing: 0, freight: 0, paid: 0 });
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-2">
        <div><label className="label">Shop</label><select className="input w-72" value={q.shopId} onChange={(e) => setQ({ ...q, shopId: e.target.value })}><option value="">Alle shops</option>{shopsQ.data?.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
        <div><label className="label">Fra</label><input className="input" type="date" value={q.from} onChange={(e) => setQ({ ...q, from: e.target.value })} /></div>
        <div><label className="label">Til</label><input className="input" type="date" value={q.to} onChange={(e) => setQ({ ...q, to: e.target.value })} /></div>
        <a className="btn" href={`/api/shipping/charges.csv?${params}`}>Hent CSV</a>
      </div>
      <table className="table card p-0">
        <thead><tr><th>Shop</th><th>Fragtfirma</th><th className="text-right">Forsendelser</th><th className="text-right">Hel</th><th className="text-right">Halv</th><th className="text-right">Kvart</th><th className="text-right">Kolli</th><th className="text-right">Pakning</th><th className="text-right">Fragt</th><th className="text-right">I alt</th><th className="text-right">Betalt i shoppen</th></tr></thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.shopId}-${r.carrier}`}>
              <td>{r.shopName}</td>
              <td>{CARRIER_NAMES[r.carrier]}</td>
              <td className="text-right tabular-nums">{r.shipments}</td>
              <td className="text-right tabular-nums">{r.palletFull}</td>
              <td className="text-right tabular-nums">{r.palletHalf}</td>
              <td className="text-right tabular-nums">{r.palletQuarter}</td>
              <td className="text-right tabular-nums">{r.colli}</td>
              <td className="text-right tabular-nums">{kr(r.packingMinor)}</td>
              <td className="text-right tabular-nums">{kr(r.freightMinor)}</td>
              <td className="text-right font-semibold tabular-nums">{kr(r.packingMinor + r.freightMinor)}</td>
              <td className="text-right tabular-nums">{kr(r.shopPaidMinor)}</td>
            </tr>
          ))}
          {rows.length > 0 && (
            <tr className="font-semibold">
              <td colSpan={7}>I alt</td>
              <td className="text-right tabular-nums">{kr(tot.packing)}</td>
              <td className="text-right tabular-nums">{kr(tot.freight)}</td>
              <td className="text-right tabular-nums">{kr(tot.packing + tot.freight)}</td>
              <td className="text-right tabular-nums">{kr(tot.paid)}</td>
            </tr>
          )}
          {rows.length === 0 && <tr><td colSpan={11} className="text-muted">Ingen forsendelser med fragtpriser i perioden. Slå fragtpriser til på shoppen, så beregnes de ved booking.</td></tr>}
        </tbody>
      </table>
      <EndOfDay onError={onError} />
    </div>
  );
}

function EndOfDay({ onError }: { onError: (e: unknown) => void }) {
  const warehouses = useQuery({ queryKey: ['warehouses'], queryFn: () => get<WarehouseDto[]>('/warehouses') });
  const today = new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Copenhagen' }).format(new Date());
  const [f, setF] = useState({ carrier: 'gls' as CarrierId, warehouseId: '', date: today });
  const [msg, setMsg] = useState<string | null>(null);
  const make = useMutation({
    mutationFn: () => post<{ documentIds: string[]; shipments: number; warning: string | null }>('/shipping/end-of-day', { carrier: f.carrier, warehouseId: f.warehouseId || undefined, date: f.date }),
    onSuccess: (r) => {
      for (const d of r.documentIds) window.open(`/api/documents/${d}/download`, '_blank');
      setMsg(`${r.shipments} forsendelser${r.warning ? `. ${r.warning}` : ''}`);
    },
    onError,
  });
  return (
    <section className="card space-y-2 text-sm">
      <h2 className="m-0 text-sm font-semibold uppercase tracking-wide text-muted">Afhentningsliste til chaufføren</h2>
      <div className="flex flex-wrap items-end gap-2">
        <div><label className="label">Fragtfirma</label><select className="input w-48" value={f.carrier} onChange={(e) => setF({ ...f, carrier: e.target.value as CarrierId })}>{Object.entries(CARRIER_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></div>
        <div><label className="label">Lager</label><select className="input w-32" value={f.warehouseId} onChange={(e) => setF({ ...f, warehouseId: e.target.value })}><option value="">Alle</option>{warehouses.data?.filter((w) => w.type === 'operated').map((w) => <option key={w.id} value={w.id}>{w.code}</option>)}</select></div>
        <div><label className="label">Dato</label><input className="input" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></div>
        <button className="btn btn-primary" disabled={make.isPending} onClick={() => { setMsg(null); make.mutate(); }}>Lav liste (PDF)</button>
        {msg && <span className="text-muted">{msg}</span>}
      </div>
      <div className="text-muted">Danske Fragtmænd vil have deres egen liste i to eksemplarer; den hentes med, når kontoen ikke kører testkørsel.</div>
    </section>
  );
}
